import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Language as LanguageIcon,
  Hub as HubIcon,
  VideoLibrary as VideoLibraryIcon,
  DataUsage as DataUsageIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import aptiviewLogo from '../../assets/aptiview-logo.png';

const drawerWidth = 280;
const miniDrawerWidth = 64;

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    onLogout();
    handleProfileMenuClose();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Companies', icon: <BusinessIcon />, path: '/companies' },
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Credits', icon: <CreditCardIcon />, path: '/credits' },
    { text: 'Languages', icon: <LanguageIcon />, path: '/languages' },
    { text: 'Company hub', icon: <HubIcon />, path: '/company-hub' },
    { text: 'Individual interviews', icon: <VideoLibraryIcon />, path: '/interviews' },
    { text: 'Token Usage', icon: <DataUsageIcon />, path: '/token-usage' },
  ];

  const drawer = (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: drawerOpen ? 'space-between' : 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 64,
        }}
      >
        {drawerOpen && (
          <Box
            component="img"
            src={aptiviewLogo}
            alt="AptiView"
            onClick={() => navigate('/dashboard')}
            sx={{
              height: 36,
              maxWidth: 160,
              objectFit: 'contain',
              display: 'block',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 0.85,
              },
            }}
          />
        )}
        {!drawerOpen && !isMobile && (
          <Box
            onClick={() => navigate('/dashboard')}
            sx={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '1.5px solid var(--primary-200)',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 5px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            <Box
              component="img"
              src={aptiviewLogo}
              alt="AptiView"
              sx={{
                height: 32,
                width: 126,
                maxWidth: 'none',
                objectFit: 'cover',
                objectPosition: 'left center',
                transform: 'translateX(2px)',
              }}
            />
          </Box>
        )}
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} className="drawer-toggle">
            {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
        {isMobile && (
          <IconButton onClick={handleMobileDrawerToggle} className="drawer-toggle">
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ borderColor: 'var(--border-subtle)' }} />
      <List sx={{ pt: 1.5 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          const listItem = (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={isSelected}
                sx={{
                  justifyContent: drawerOpen ? 'initial' : 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText primary={item.text} />
                )}
              </ListItemButton>
            </ListItem>
          );

          return !drawerOpen ? (
            <Tooltip key={item.text} title={item.text} placement="right">
              {listItem}
            </Tooltip>
          ) : (
            listItem
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: {
            md: `calc(100% - ${drawerOpen ? drawerWidth : miniDrawerWidth}px)`,
          },
          ml: {
            md: `${drawerOpen ? drawerWidth : miniDrawerWidth}px`,
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={aptiviewLogo}
            alt="AptiView"
            onClick={() => navigate('/dashboard')}
            sx={{
              height: 28,
              maxWidth: 120,
              objectFit: 'contain',
              mr: 2,
              display: { xs: 'block', md: 'none' },
              cursor: 'pointer',
              filter: 'brightness(0) invert(1)',
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              <AccountCircleIcon />
            </Avatar>
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: drawerOpen ? drawerWidth : miniDrawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerOpen ? drawerWidth : miniDrawerWidth,
              backgroundColor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2.5, sm: 3.5 },
          width: {
            md: `calc(100% - ${drawerOpen ? drawerWidth : miniDrawerWidth}px)`
          },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'var(--bg-app)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
